import type { ExportData, Foyer } from '@/engine/types';
import { getRepository } from './repository-factory';

export function validateExportData(data: unknown): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!data || typeof data !== 'object') {
    return { valid: false, errors: ['Invalid data format'] };
  }

  const d = data as Record<string, unknown>;

  if (typeof d.version !== 'number') errors.push('Missing version');
  if (typeof d.exportedAt !== 'string') errors.push('Missing exportedAt');
  if (!d.foyer || typeof d.foyer !== 'object') errors.push('Missing foyer');
  if (!Array.isArray(d.blocks)) errors.push('blocks must be an array');
  if (!Array.isArray(d.domains)) errors.push('domains must be an array');
  if (!Array.isArray(d.categories)) errors.push('categories must be an array');
  if (!Array.isArray(d.lines)) errors.push('lines must be an array');
  if (!Array.isArray(d.objectives)) errors.push('objectives must be an array');
  if (!Array.isArray(d.scenarios)) errors.push('scenarios must be an array');
  if (!Array.isArray(d.budgetSnapshots)) errors.push('budgetSnapshots must be an array');
  if (!Array.isArray(d.patrimoineLines)) errors.push('patrimoineLines must be an array');
  if (!Array.isArray(d.patrimoineCategories)) errors.push('patrimoineCategories must be an array');
  if (!Array.isArray(d.patrimoineSnapshots)) errors.push('patrimoineSnapshots must be an array');
  if (!d.kpiThresholds || typeof d.kpiThresholds !== 'object') errors.push('Missing kpiThresholds');

  if (d.foyer && typeof d.foyer === 'object') {
    const foyer = d.foyer as Record<string, unknown>;
    if (!foyer.id || typeof foyer.id !== 'string') errors.push('foyer.id is required');
    if (!['solo', 'couple'].includes(foyer.mode as string)) errors.push('foyer.mode must be solo or couple');
    if (!foyer.name || typeof foyer.name !== 'string') errors.push('foyer.name is required');
  }

  return { valid: errors.length === 0, errors };
}

export function generateExportFilename(foyerName: string): string {
  const slug = foyerName
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  const date = new Date().toISOString().split('T')[0];
  return `budget-${slug}-export-${date}.json`;
}

export function checkModeCompatibility(
  importMode: 'solo' | 'couple',
  currentMode: 'solo' | 'couple',
): 'compatible' | 'couple-to-solo' | 'solo-to-couple' {
  if (importMode === currentMode) return 'compatible';
  if (importMode === 'couple' && currentMode === 'solo') return 'couple-to-solo';
  return 'solo-to-couple';
}

export async function exportAllData(): Promise<{ data: ExportData; filename: string }> {
  const repo = getRepository();
  const raw = await repo.exportAll() as Record<string, unknown[]>;

  const foyer = (raw.foyers as Foyer[])[0];

  const data: ExportData = {
    version: 1,
    exportedAt: new Date().toISOString(),
    foyer,
    blocks: raw.blocks as ExportData['blocks'],
    domains: raw.domains as ExportData['domains'],
    categories: raw.categories as ExportData['categories'],
    lines: raw.lines as ExportData['lines'],
    objectives: raw.objectives as ExportData['objectives'],
    scenarios: raw.scenarios as ExportData['scenarios'],
    budgetSnapshots: raw.budgetSnapshots as ExportData['budgetSnapshots'],
    patrimoineLines: raw.patrimoineLines as ExportData['patrimoineLines'],
    patrimoineCategories: raw.patrimoineCategories as ExportData['patrimoineCategories'],
    patrimoineSnapshots: raw.patrimoineSnapshots as ExportData['patrimoineSnapshots'],
    kpiThresholds: (raw.kpiThresholds as ExportData['kpiThresholds'][])[0],
  };

  return { data, filename: generateExportFilename(foyer.name) };
}

export function downloadJson(data: ExportData, filename: string): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// AUD-003 : les anciens exports cloud émettaient les répartitions à plat
// (ratioP1/ratioP2) alors que le moteur lit `ratio` imbriqué — on normalise
// à l'import pour qu'aucune sauvegarde, quel que soit son âge ou le mode de
// stockage, ne perde ses répartitions personnalisées.
function normalizeRatios<T>(rows: T[]): T[] {
  return rows.map((r) => {
    const row = r as Record<string, unknown>;
    if (row.ratio == null && typeof row.ratioP1 === 'number' && typeof row.ratioP2 === 'number') {
      return { ...row, ratio: { p1: row.ratioP1, p2: row.ratioP2 } } as T;
    }
    return r;
  });
}

export async function importAllData(data: ExportData): Promise<void> {
  const repo = getRepository();
  await repo.importAll({
    foyers: [data.foyer],
    blocks: data.blocks,
    domains: normalizeRatios(data.domains),
    categories: normalizeRatios(data.categories),
    lines: normalizeRatios(data.lines),
    objectives: data.objectives,
    scenarios: data.scenarios,
    budgetSnapshots: data.budgetSnapshots,
    patrimoineLines: data.patrimoineLines,
    patrimoineCategories: data.patrimoineCategories,
    patrimoineSnapshots: data.patrimoineSnapshots,
    kpiThresholds: data.kpiThresholds ? [data.kpiThresholds] : [],
  });
}

export async function deleteAllData(): Promise<void> {
  const repo = getRepository();
  await repo.deleteAll();
}
